import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useOffline } from "../../offline/OfflineContext";
import { countUnsentEntriesOnDevice } from "../../offline/unsentEntries";
import { useSessionActions } from "./SessionActions";

/**
 * What the account menu hands to the page with the sign-out through the router state (F-74): `confirm`
 * when unsent entries need the confirmation before the sign-out, `failed` when the sign-out failed and
 * the person is still signed in. „Mehr“ hands the same over to „Einstellungen“ when the window becomes
 * wide while the confirmation or the error is shown (F-84). The module lies in `shared/` because the
 * shells of both areas (`app/`, `features/platform`) use it (ADR-0208).
 */
export type SignOutRequest = "confirm" | "failed";

/** Router state that carries a {@link SignOutRequest}. */
export interface SignOutRequestState {
  /** The request. */
  readonly signOut: SignOutRequest;
}

/** Fragment of the account section on the settings pages; it receives the focus. */
export const accountSectionId = "account";

/**
 * Reads a {@link SignOutRequest} from the router state.
 *
 * @param state - The state of the location.
 * @returns The request, or `null` if the state carries none.
 */
export function readSignOutRequest(state: unknown): SignOutRequest | null {
  let request: SignOutRequest | null = null;

  if (typeof state === "object" && state !== null && "signOut" in state) {
    const value = state.signOut;
    request = value === "confirm" || value === "failed" ? value : null;
  }

  return request;
}

/** A {@link SignOutRequest} taken from the router state, with the key of its arrival. */
export interface SignOutHandover {
  /** The request. */
  readonly request: SignOutRequest;
  /** Key of the location that carried it; a new request of the menu has a new key. */
  readonly key: string;
}

/**
 * Takes a {@link SignOutRequest} of the account menu from the router state (F-74) and removes it
 * from the history entry right away (a replacing navigation without state), so reloading the page
 * or coming back to it does not show the confirmation or the error again. The request stays with
 * the page until the menu hands over the next one.
 *
 * @returns The last request handed over, or `null`.
 */
export function useSignOutHandover(): SignOutHandover | null {
  const location = useLocation();
  const navigate = useNavigate();
  const incoming = readSignOutRequest(location.state);
  const [handover, setHandover] = useState<SignOutHandover | null>(null);

  if (incoming !== null && handover?.key !== location.key) {
    setHandover({ request: incoming, key: location.key });
  }

  const { pathname, search, hash } = location;

  useEffect(() => {
    if (incoming !== null) {
      void navigate({ pathname, search, hash }, { replace: true, state: null });
    }
  }, [incoming, navigate, pathname, search, hash]);

  return handover;
}

/**
 * Returns a function that counts the entries not yet sent from this device, clock events and project
 * attributions of every owner, because the sign-out deletes them all (F-84). It reads the store at the
 * moment of the call: observed data may not be loaded yet (F-57).
 *
 * @returns The counting function.
 */
function useCountUnsent(): () => Promise<number> {
  const { store } = useOffline();
  const count = async (): Promise<number> => {
    const pending = await countUnsentEntriesOnDevice(store);
    return pending;
  };

  return count;
}

/**
 * Returns the request that hands an open confirmation or a shown error of a {@link GuardedSignOut} on
 * to another page, for example from „Mehr“ to „Einstellungen“ when the window becomes wide (F-84).
 *
 * @param signOut - The sign-out of the page that is left.
 * @returns The request, or `null` if neither is shown.
 */
export function pendingSignOutRequest(signOut: GuardedSignOut): SignOutRequest | null {
  const request: SignOutRequest | null = signOut.confirming
    ? "confirm"
    : signOut.failed
      ? "failed"
      : null;

  return request;
}

/** The sign-out of the settings pages with its confirmation and error. */
export interface GuardedSignOut {
  /** Number of unsent entries the open confirmation names. */
  readonly pendingCount: number;
  /** Whether the confirmation is open. */
  readonly confirming: boolean;
  /** Whether the last sign-out failed and the person is still signed in. */
  readonly failed: boolean;
  /** Signs out, or opens the confirmation first while entries are unsent. */
  readonly request: () => Promise<void>;
  /** Signs out after the confirmation. */
  readonly confirm: () => void;
  /** Closes the confirmation. */
  readonly cancel: () => void;
}

/**
 * Sign-out of the settings pages (`/more`, `/settings`) and of the account page of the operator
 * area (`/platform/account`, F-84): unsent entries are named in a confirmation
 * before the local data is deleted, and a failed sign-out says so instead of failing silently
 * (F-57). A request of the account menu (F-74) shows the error on arrival, or counts the unsent
 * entries again and opens the confirmation, or signs out at once if they were sent meanwhile.
 *
 * @param initial - The request the account menu handed over, or `null`.
 * @returns The sign-out.
 */
export function useGuardedSignOut(initial: SignOutRequest | null): GuardedSignOut {
  const { signOut } = useSessionActions();
  const countUnsent = useCountUnsent();
  const [pendingCount, setPendingCount] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(initial === "failed");
  const askOnArrivalRef = useRef(initial === "confirm");

  const run = async (): Promise<void> => {
    setFailed(false);

    try {
      await signOut();
    } catch {
      setFailed(true);
    }
  };
  const request = async (): Promise<void> => {
    let pending: number;

    try {
      pending = await countUnsent();
    } catch {
      // The device store does not answer: never sign out unasked over unsent entries, and never
      // leave the button without an answer (F-126).
      setFailed(true);
      return;
    }

    if (pending > 0) {
      setPendingCount(pending);
      setConfirming(true);
    } else {
      await run();
    }
  };

  // Counted again: entries sent meanwhile need no confirmation, the sign-out then runs at once.
  const requestOnArrival = useEffectEvent(() => {
    void request();
  });

  useEffect(() => {
    if (askOnArrivalRef.current) {
      askOnArrivalRef.current = false;
      requestOnArrival();
    }
  }, []);

  const guarded: GuardedSignOut = {
    pendingCount,
    confirming,
    failed,
    request,
    confirm: () => {
      setConfirming(false);
      void run();
    },
    cancel: () => {
      setConfirming(false);
    },
  };

  return guarded;
}

/**
 * Sign-out of the account menu (F-74). Without unsent entries it signs out at once through the
 * shared sign-out (`shared/session/signOut.ts`). Unsent entries or a failure lead to the account
 * section of the page with the sign-out, which shows the confirmation or the error there with the
 * focus on the section, so the menu needs no dialog of its own: „Einstellungen“ in the application
 * of the tenants, the account page in the operator area (F-84).
 *
 * @param accountPath - Address of the page with the sign-out, without fragment; the fragment
 * {@link accountSectionId} is added.
 * @returns The function the menu entry runs.
 */
export function useMenuSignOut(accountPath: string): () => void {
  const { signOut } = useSessionActions();
  const countUnsent = useCountUnsent();
  const navigate = useNavigate();
  const toSettings = (request: SignOutRequest): void => {
    const state: SignOutRequestState = { signOut: request };
    void navigate(`${accountPath}#${accountSectionId}`, { state });
  };
  const run = async (): Promise<void> => {
    const pending = await countUnsent();

    if (pending > 0) {
      toSettings("confirm");
      return;
    }

    try {
      await signOut();
    } catch {
      toSettings("failed");
    }
  };
  const signOutFromMenu = (): void => {
    void run();
  };

  return signOutFromMenu;
}
